/**
 * ============================================================================
 * The Official Attendance Record (H8 §5)
 * ============================================================================
 * The document A-B Tech asks for at the end of a class. Every header field the
 * regulation lists is on the page, and every one of them comes from the frozen
 * published snapshot rather than the live section — so a record handed over in
 * September still reads the same in March.
 *
 * WATERMARKED DRAFT UNTIL CERTIFIED, and that is load-bearing rather than
 * decorative. Certification is the lead instructor's signed statement of
 * accuracy, and it is the single most common audit finding when it is missing
 * (H8 finding #1). An uncertified record that printed like a final one would be
 * the most dangerous thing this screen could do — so the watermark sits behind
 * the content, survives printing, and the page says in words what it means.
 *
 * Certification is per SESSION (attendance_certifications is unique per
 * session), so a tailgate with eight dates is only fully certified when all
 * eight are signed. A part-signed section stays a draft.
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { reportingPeriodLabel } from '../lib/sections.js';

const DELIVERY_LABELS = {
  TR: 'TR — Traditional',
  IN: 'IN — Internet',
  BL: 'BL — Blended',
  HY: 'HY — Hybrid',
  HF: 'HF — HyFlex',
};

function hours(n) {
  if (n === null || n === undefined) return '—';
  return `${(Math.round(Number(n) * 100) / 100).toFixed(2)}`;
}

export function AttendanceRecordPage() {
  const { id } = useParams();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    apiFetch(`/api/v1/classes/${id}/attendance-record`)
      .then((data) => active && setRecord(data))
      .catch((err) => active && setError(err));
    return () => {
      active = false;
    };
  }, [id]);

  if (error) {
    return (
      <>
        <p className="crumb">
          <Link to={`/sections/${id}`}>← Back to section</Link>
        </p>
        <ErrorBanner error={error} />
      </>
    );
  }

  if (!record) {
    return (
      <p className="muted" aria-busy="true">
        Building the record…
      </p>
    );
  }

  const h = record.header;
  const certified = record.certification.certified;
  const code = [h.course_prefix, h.course_number].filter(Boolean).join(' ');
  const fullCode = h.section_number ? `${code}-${h.section_number}` : code;

  return (
    <>
      <p className="crumb no-print">
        <Link to={`/sections/${id}`}>← Back to section</Link>
      </p>

      <div className="toolbar no-print">
        <button type="button" className="btn btn--primary btn--auto" onClick={() => window.print()}>
          Print this record
        </button>
        <Link className="btn btn--auto" to={`/sections/${id}/packet`}>
          Whole closing packet
        </Link>
      </div>

      {!certified && (
        <p className="callout callout--warn no-print">
          <strong>
            This is a DRAFT. {record.certification.certified_sessions} of{' '}
            {record.certification.total_sessions} sessions have been certified.
          </strong>
          <span className="callout__detail">
            It becomes the official record once the lead instructor signs the certification of
            accuracy for every session. Printing it now is fine for checking — it will carry the
            DRAFT watermark.
          </span>
        </p>
      )}

      {!record.re_identification.available && (
        <p className="callout callout--warn no-print">
          <strong>Some attendance on this record cannot be matched to a person by your account.</strong>
          <span className="callout__detail">
            {hours(record.re_identification.unattributed_minutes / 60)} hours are recorded but
            unattributed. Naming participants against their attendance is restricted to the
            instructor who ran the session, or to an account holding “identity.reveal”. Ask one of
            them to produce this record.
          </span>
        </p>
      )}

      {/* aria-hidden: the words "DRAFT — NOT CERTIFIED" already appear in the
          heading below, so a screen reader would otherwise hear them twice. */}
      <article className={certified ? 'record' : 'record record--draft'}>
        {!certified && (
          <div className="record__watermark" aria-hidden="true">
            DRAFT
          </div>
        )}

        <header className="record__head">
          <h1>
            Official Attendance Record
            {!certified && <span className="record__flag"> — DRAFT, NOT CERTIFIED</span>}
          </h1>
          <p className="muted">
            Asheville-Buncombe Technical Community College · Division of Continuing Education
          </p>
        </header>

        <dl className="record__grid">
          <div>
            <dt>Course</dt>
            <dd>
              <strong>{fullCode || h.ccl_course_code || '—'}</strong>
              {h.ccl_course_code && fullCode !== h.ccl_course_code && (
                <span className="muted"> · {h.ccl_course_code}</span>
              )}
            </dd>
          </div>
          <div>
            <dt>Title</dt>
            <dd>{h.official_ccl_title || h.working_title || '—'}</dd>
          </div>
          <div>
            <dt>Instructor(s)</dt>
            <dd>{record.instructors.length ? record.instructors.join(', ') : '—'}</dd>
          </div>
          <div>
            <dt>Dates</dt>
            <dd>
              {h.start_date && h.end_date ? `${h.start_date} to ${h.end_date}` : '—'}
            </dd>
          </div>
          <div>
            <dt>Census date</dt>
            <dd>
              {h.census_date ?? (
                <span className="muted">
                  None — {h.section_schedule_type === 'non_regular'
                    ? 'non-regularly scheduled, funded on actual contact hours'
                    : 'not applicable'}
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt>Delivery method</dt>
            <dd>{DELIVERY_LABELS[h.delivery_method] ?? h.delivery_method ?? '—'}</dd>
          </div>
          <div>
            <dt>Scheduled hours</dt>
            <dd>
              {hours(h.scheduled_hours)}
              {h.state_approved_hours !== null && (
                <>
                  {' '}
                  <span className="muted">
                    · CCL approved {hours(h.state_approved_hours)}
                  </span>{' '}
                  {h.hours_reconcile ? (
                    <span className="badge badge--published">Reconciles</span>
                  ) : (
                    <span className="badge">Does not reconcile</span>
                  )}
                </>
              )}
            </dd>
          </div>
          <div>
            <dt>Reporting period</dt>
            <dd>{reportingPeriodLabel(h.reporting_period) ?? '—'}</dd>
          </div>
        </dl>

        <h2>Schedule</h2>
        <div className="schedule__scroll">
          <table className="schedule__table">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Start</th>
                <th scope="col">End</th>
                <th scope="col">Location</th>
              </tr>
            </thead>
            <tbody>
              {record.meeting_schedule.map((m, i) => (
                <tr key={`${m.date}-${i}`}>
                  <td>{m.date}</td>
                  <td>{m.start}</td>
                  <td>{m.end}</td>
                  <td>{m.location || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2>
          Participants <span className="muted">({record.totals.enrolled})</span>
        </h2>
        <div className="schedule__scroll">
          <table className="schedule__table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Student ID</th>
                <th scope="col">Name</th>
                <th scope="col">Agency of record</th>
                <th scope="col">Position</th>
                <th scope="col">P/V</th>
                <th scope="col">Fee</th>
                <th scope="col">Contact hrs</th>
              </tr>
            </thead>
            <tbody>
              {record.participants.map((p, i) => (
                <tr key={p.enrollment_id}>
                  <td className="muted">{i + 1}</td>
                  <td>
                    {p.identity_status === 'unconfirmed' ? (
                      <span className="record__flag">ID unconfirmed</span>
                    ) : (
                      <code>{p.external_id ?? '—'}</code>
                    )}
                  </td>
                  <td>
                    {p.full_name ?? '—'}
                    {p.withdrawal_date && (
                      <span className="muted"> · withdrew {p.withdrawal_date}</span>
                    )}
                  </td>
                  <td>{p.agency_name}</td>
                  <td>{p.role_title ?? '—'}</td>
                  <td>{p.worker_type === 'volunteer' ? 'V' : p.worker_type === 'paid' ? 'P' : '—'}</td>
                  {/* 'pending' is not 'invoiced': the waiver question has not
                      been settled yet (M15). Printing it as a bill would be a
                      false statement on an official record. */}
                  <td>
                    {p.fee_status === 'waived'
                      ? p.waiver_code ?? 'Waived'
                      : p.fee_status === 'pending'
                        ? 'Pending'
                        : 'Invoiced'}
                  </td>
                  <td>{p.attended ? hours(p.contact_hours) : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="record__totals">
          <strong>{record.totals.attended}</strong> of {record.totals.enrolled} attended ·{' '}
          <strong>{hours(record.totals.contact_hours)}</strong> total contact hours
          <span className="muted"> · 60 minutes = 1 hour</span>
        </p>

        {/* The certification of accuracy. Rendered whether signed or not,
            because its absence is exactly what an auditor looks for. */}
        <section className="record__cert">
          <h2>Certification of accuracy</h2>
          <p>
            I certify that the attendance recorded above is a true and accurate record of the
            instruction delivered and of the persons who attended.
          </p>

          {certified ? (
            <ul className="plain-list">
              {record.certification.signatures.map((s) => (
                <li key={s.session_id}>
                  Signed {new Date(s.certified_at).toLocaleString()}
                  <span className="muted"> · statement {s.cert_statement_version}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="record__unsigned">
              <strong>Not yet certified.</strong> This record is not official until the lead
              instructor of record signs it. {record.certification.certified_sessions} of{' '}
              {record.certification.total_sessions} sessions signed.
            </p>
          )}
        </section>
      </article>
    </>
  );
}

export default AttendanceRecordPage;
